import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Mail,
  Shield,
  Calendar,
  Edit3,
  Key,
  Trash2,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { EditProfileModal } from '@/components/user/EditProfileModal';
import { DeleteConfirmModal } from '@/components/admin/DeleteConfirmModal';
import { SuccessModal } from '@/components/admin/SuccessModal';
import { ConnectionsManager } from '@/components/user/ConnectionsManager';
import {
  db,
  doc,
  updateDoc,
  sendPasswordResetEmail,
  auth,
  callFunction,
} from '@/lib/firebase';

export const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut, refreshUserData } = useAuth();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState({ title: '', message: '' });
  const [actionLoading, setActionLoading] = useState(false);

  if (!user) {
    navigate('/login');
    return null;
  }

  const handleUpdateProfile = async (data: { displayName: string }) => {
    if (!user) return;

    try {
      await updateDoc(doc(db, 'users', user.id), {
        displayName: data.displayName,
      });

      await refreshUserData();

      setSuccessMessage({
        title: 'Profilo Aggiornato!',
        message: 'Le tue informazioni sono state aggiornate con successo.'
      });
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error updating profile:', error);
      throw new Error('Errore durante l\'aggiornamento del profilo');
    }
  };

  const handleChangePassword = async () => {
    if (!user) return;

    try {
      setActionLoading(true);
      await sendPasswordResetEmail(auth, user.email);
      setSuccessMessage({
        title: 'Email Inviata!',
        message: `Abbiamo inviato un link per il reset della password a ${user.email}.`
      });
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error sending password reset email:', error);
      setSuccessMessage({
        title: 'Errore',
        message: 'Errore durante l\'invio dell\'email. Riprova.'
      });
      setShowSuccessModal(true);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    try {
      setActionLoading(true);
      const deleteUserFunction = callFunction<{ userId: string }, any>('deleteUser');
      await deleteUserFunction({ userId: user.id });

      // L'utente verrà automaticamente disconnesso dopo l'eliminazione
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error deleting account:', error);
      setShowDeleteModal(false);
      setSuccessMessage({
        title: 'Errore',
        message: 'Errore durante l\'eliminazione dell\'account. Riprova.'
      });
      setShowSuccessModal(true);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'superadmin':
        return 'Super Admin';
      case 'admin':
        return 'Amministratore';
      case 'user':
      default:
        return 'Utente';
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'superadmin':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'user':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Profilo</h1>
          <p className="text-gray-600 mt-1">Gestisci il tuo account e le impostazioni</p>
        </div>

        {/* User Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>Informazioni Account</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Avatar & Name */}
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-wine-purple to-wine-purple/80 flex items-center justify-center">
                  <User className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{user.displayName}</h2>
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mt-1 ${getRoleBadgeColor(
                      user.role
                    )}`}
                  >
                    {getRoleLabel(user.role)}
                  </span>
                </div>
              </div>

              {/* User Details */}
              <div className="space-y-4 pt-4 border-t border-gray-200">
                <InfoRow icon={<Mail className="w-5 h-5" />} label="Email" value={user.email} />
                <InfoRow
                  icon={<Shield className="w-5 h-5" />}
                  label="Ruolo"
                  value={getRoleLabel(user.role)}
                />
                <InfoRow
                  icon={<Calendar className="w-5 h-5" />}
                  label="Membro dal"
                  value={formatDate(user.createdAt)}
                />
                {user.lastLogin && (
                  <InfoRow
                    icon={<Calendar className="w-5 h-5" />}
                    label="Ultimo accesso"
                    value={formatDate(user.lastLogin)}
                  />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Settings Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {/* Edit Profile */}
              <Button
                variant="outline"
                icon={<Edit3 className="w-5 h-5" />}
                onClick={() => setShowEditModal(true)}
                fullWidth
              >
                Modifica Profilo
              </Button>

              {/* Change Password */}
              <Button
                variant="outline"
                icon={<Key className="w-5 h-5" />}
                onClick={handleChangePassword}
                loading={actionLoading}
                fullWidth
              >
                Cambia Password
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Connections Manager */}
        <ConnectionsManager />

        {/* Danger Zone Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-red-600">Zona Pericolosa</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
              <p className="text-sm text-red-800 font-medium mb-1">
                Attenzione: Azione Irreversibile
              </p>
              <p className="text-sm text-red-700">
                L'eliminazione del tuo account comporterà la cancellazione permanente di tutti i tuoi dati,
                inclusi tutti i vini salvati in cantina. Questa azione non può essere annullata.
              </p>
            </div>
            <Button
              variant="danger"
              icon={<Trash2 className="w-5 h-5" />}
              onClick={() => setShowDeleteModal(true)}
              fullWidth
            >
              Elimina Account
            </Button>
          </CardContent>
        </Card>

        {/* Modals */}
        <EditProfileModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleUpdateProfile}
          user={user}
        />

        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteAccount}
          title="Elimina Account"
          message="Stai per eliminare definitivamente il tuo account e tutti i tuoi vini salvati. Questa azione NON può essere annullata. Tutti i tuoi dati verranno persi per sempre."
          itemName={`il tuo account (${user.email})`}
          loading={actionLoading}
        />

        <SuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          title={successMessage.title}
          message={successMessage.message}
          autoCloseMs={3000}
        />
      </div>
    </Layout>
  );
};

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value }) => {
  return (
    <div className="flex items-center gap-3">
      <div className="text-wine-purple">{icon}</div>
      <div className="flex-1">
        <p className="text-sm text-gray-600">{label}</p>
        <p className="text-base font-medium text-gray-900">{value}</p>
      </div>
    </div>
  );
};
